<template>
  <div class="event-statistics-page">
    <!-- 顶部标题 -->
    <div class="page-header">
      <div class="header-left" @click="goBack">
        <van-icon name="arrow-left" />
        <span>返回</span>
      </div>
      <h1 class="page-title">赛事统计</h1>
      <div class="header-right"></div>
    </div>

    <!-- 搜索框 -->
    <div class="search-container">
      <van-search
        v-model="search.info"
        shape="round"
        placeholder="搜索赛事名称"
        show-action
      >
        <template #action>
          <div @click="onSearch" class="search-action">搜索</div>
        </template>
      </van-search>
    </div>

    <!-- Tab 切换 -->
    <van-tabs
      v-model="activeTab"
      @change="onTabChange"
      animated
      swipeable
      color="#2D8CF0"
      title-active-color="#2D8CF0"
      title-inactive-color="#666"
    >
      <van-tab title="报名中" name="ongoing">
        <template #title>
          <div class="tab-title">
            <van-icon name="clock-o" />
            <span>报名中</span>
          </div>
        </template>
      </van-tab>
      <van-tab title="报名结束" name="ended">
        <template #title>
          <div class="tab-title">
            <van-icon name="completed" />
            <span>报名结束</span>
          </div>
        </template>
      </van-tab>
    </van-tabs>

    <!-- 年度筛选 -->
    <div class="year-filter">
      <div class="year-list">
        <div
          v-for="year in yearList"
          :key="year"
          :class="['year-item', { active: selectedYear === year }]"
          @click="selectYear(year)"
        >
          <span>{{ year }}年</span>
          <div v-if="selectedYear === year" class="year-indicator"></div>
        </div>
      </div>
    </div>
    <!-- 排序按钮 -->
    <div class="sort-buttons">
      <div
        :class="['sort-btn', { active: sortType === 'time' }]"
        @click="sortByTime"
      >
        <van-icon name="clock-o" />
        <span>按时间</span>
        <van-icon
          v-if="sortType === 'time'"
          :name="sortOrder === 'desc' ? 'arrow-down' : 'arrow-up'"
          class="sort-icon"
        />
      </div>
      <div
        :class="['sort-btn', { active: sortType === 'people' }]"
        @click="sortByPeople"
      >
        <van-icon name="friends-o" />
        <span>按人数</span>
        <van-icon
          v-if="sortType === 'people'"
          :name="sortOrder === 'desc' ? 'arrow-down' : 'arrow-up'"
          class="sort-icon"
        />
      </div>
      <div
        :class="['sort-btn', { active: sortType === 'progress' }]"
        @click="sortByProgress"
      >
        <van-icon name="bar-chart-o" />
        <span>按进度</span>
        <van-icon
          v-if="sortType === 'progress'"
          :name="sortOrder === 'desc' ? 'arrow-down' : 'arrow-up'"
          class="sort-icon"
        />
      </div>
    </div>

    <!-- 赛事列表 -->
    <van-pull-refresh v-model="isLoading" @refresh="onRefresh" :disabled="!isAtTop">
      <div class="event-list" ref="eventList">
        <van-collapse v-model="activeNames" accordion>
          <van-collapse-item
            v-for="(item, index) in eventList"
            :key="index"
            :name="item.SSID"
          >
            <!-- 卡片头部 -->
            <template #title>
              <div class="event-card" @click="getItemData(item.SSID)">
                <div class="event-header">
                  <h3 class="event-name">{{ item.SSMC }}</h3>
                </div>

                <div class="event-stats">
                  <div class="event-time">
                    <div class="time-row">
                      <van-icon name="clock-o" />
                      <span>报名开始：{{ item.BMQSRQ }}</span>
                    </div>
                    <div class="time-row">
                      <van-icon name="clock-o" />
                      <span>报名结束：{{ item.BMJZRQ }}</span>
                    </div>
                    <div class="time-row">
                      <van-icon name="calendar-o" />
                      <span>开赛日期：{{ item.BSQSRQ }}</span>
                    </div>
                  </div>

                  <div class="days-left">
                    <van-icon name="warning-o" />
                    <span v-if="item.SYTS > 0"
                      >距离报名结束还有
                      <strong>{{ item.SYTS }}</strong> 天</span
                    >
                    <span v-else class="expired">报名已结束</span>
                  </div>

                  <!-- 报名进度条 -->
                  <div class="registration-progress">
                    <div class="progress-header">
                      <span class="progress-label">报名进度</span>
                      <span class="progress-text"
                        >{{ item.BMRS }}/{{ item.ALLRSXZ }}人（{{
                          getProgressPercentage(item.BMRS, item.ALLRSXZ)
                        }}%）</span
                      >
                    </div>
                    <div class="progress-bar">
                      <div
                        class="progress-fill"
                        :class="getProgressClass(item.BMRS, item.ALLRSXZ)"
                        :style="{
                          width:
                            getProgressPercentage(item.BMRS, item.ALLRSXZ) + '%'
                        }"
                      ></div>
                    </div>
                  </div>
                  <div class="stat-item">
                    <van-icon name="friends-o" color="#E91E63" />
                    <span class="stat-label">预期人数：</span>
                    <span class="stat-value">{{ item.ALLRSXZ }}</span>
                  </div>
                  <div class="stat-item">
                    <van-icon name="friends-o" color="#2D8CF0" />
                    <span class="stat-label">已报人次：</span>
                    <span class="stat-value">
                      {{ item.BMRC }}
                    </span>
                  </div>
                  <div class="stat-item">
                    <van-icon name="user-o" color="#19BE6B" />
                    <span class="stat-label">已报人数：</span>
                    <span class="stat-value">{{ item.BMRS }}</span>
                  </div>

                  <div class="stat-item">
                    <van-icon name="balance-pay" color="#FF9900" />
                    <span class="stat-label">线上金额：</span>
                    <span class="stat-value primary">¥{{ item.ONLINEJE }}</span>
                  </div>
                  <div class="stat-item">
                    <van-icon name="cash-back-record" color="#FF6B00" />
                    <span class="stat-label">线下金额：</span>
                    <span class="stat-value primary">¥{{ item.XXZJE }}</span>
                  </div>
                  <div class="stat-item total-amount">
                    <van-icon name="gold-coin-o" color="#E53935" />
                    <span class="stat-label">总金额：</span>
                    <span class="stat-value total"
                      >¥{{ item.ONLINEJE - 0 + (item.XXZJE - 0) }}</span
                    >
                  </div>
                  <div class="stat-item">
                    <van-icon name="eye-o" color="#00BCD4" />
                    <span class="stat-label">访问次数：</span>
                    <span class="stat-value">{{ item.FWCS }}</span>
                  </div>
                  <div class="stat-item">
                    <van-icon name="manager-o" color="#9C27B0" />
                    <span class="stat-label">负责人：</span>
                    <span class="stat-value">{{ item.SQLXR }}</span>
                  </div>
                </div>

                <!-- 场馆和费用信息 -->
                <div class="venue-info">
                  <div class="info-row">
                    <van-icon name="location-o" />
                    <span class="info-label">比赛场馆：</span>
                    <span class="info-value">{{ item.CGMC }}</span>
                  </div>
                  <div class="info-row">
                    <van-icon name="cash-on-deliver" />
                    <span class="info-label">报名单价：</span>
                    <span class="info-value highlight">¥{{ item.DJ }}/人</span>
                  </div>
                  <div class="info-row">
                    <van-icon name="shop-o" />
                    <span class="info-label">场地数量：</span>
                    <span class="info-value">{{ item.CDCOUNT }}个</span>
                  </div>
                </div>
              </div>
            </template>
            <!-- 折叠内容：项目详情 -->
            <div class="event-detail" v-if="itemList.length > 0">
              <div class="detail-header">
                <van-icon name="bars" />
                <span>项目报名详情</span>
              </div>

              <div class="category-list">
                <div
                  v-for="(mxItem, mxIndex) in itemList"
                  :key="mxIndex"
                  class="category-item"
                >
                  <div class="category-name">
                    <van-icon name="medal-o" color="#FF6B6B" />
                    {{ mxItem.XMJC + mxItem.SSLXMC }}
                  </div>
                  <div class="category-count">
                    <span class="count-number">{{ mxItem.ZTM }}</span>
                    <span class="count-unit">{{ mxItem.DW }}</span>
                  </div>
                </div>
              </div>
            </div>
          </van-collapse-item>
        </van-collapse>

        <div
          style="text-align: center;margin-top: 15px;font-size:13px;color:rgb(104, 104, 104)"
          @click="loadingGdHandler"
          v-if="eventList.length > 0"
        >
          <span>{{ isEndPage ? "已加载全部" : "点击加载更多" }}</span>
          <van-icon name="arrow-down" v-if="!isEndPage" />
        </div>

        <!-- 空状态 -->
        <van-empty
          v-if="eventList.length === 0"
          :description="
            activeTab === 'ongoing' ? '暂无报名中的赛事' : '暂无已结束的赛事'
          "
          image="search"
        />
      </div>
    </van-pull-refresh>

    <!-- 返回顶部按钮 -->
    <transition name="fade">
      <div v-show="showBackTop" class="back-top-btn" @click="scrollToTop">
        <van-icon name="back-top" />
        <span>顶部</span>
      </div>
    </transition>

    <loading-com />
  </div>
</template>

<script>
import HeaderCom from "../commonCom/HeaderCom.vue";
import LoadingCom from "../commonCom/LoadingCom.vue";

export default {
  name: "EventStatistics",
  components: { HeaderCom, LoadingCom },
  data() {
    return {
      yearList: [2023, 2024, 2025], // 年度列表
      isEndPage: false,
      isLoading: false,
      activeNames: [],
      activeTab: "ongoing", // 当前激活的 tab: 'ongoing' 报名中 或 'ended' 报名结束
      sortType: "time", // 排序类型: 'time' 报名时间、'people' 人数 或 'progress' 进度，默认按时间
      sortOrder: "desc", // 排序顺序: 'asc' 升序 或 'desc' 降序
      refreshing: false, // 下拉刷新状态
      showBackTop: false, // 是否显示返回顶部按钮
      openIdEn: "", // 用户标识
      op: "sTongJiGl", // 后端处理器名称
      searchKeyword: "", // 搜索关键词
      eventList: [], // 从接口获取数据
      pageSet: {
        currentPage: 1, //当前页页码
        pageSize: 5, //每页默认显示的条数
        totalNums: 0 //数据总条数
      },
      search: {
        bszt: "1",
        info: ""
      },
      itemList: [],
      selectedYear: new Date().getFullYear(),
      isAtTop: true // 是否在顶部
    };
  },
  computed: {
    // 根据 tab 过滤赛事列表
    filteredEventList() {
      if (this.activeTab === "ongoing") {
        // 报名中：daysLeft >= 0
        return this.eventList.filter(event => event.daysLeft >= 0);
      } else {
        // 报名结束：daysLeft < 0
        return this.eventList.filter(event => event.daysLeft < 0);
      }
    }
  },
  created() {
    this.utilPost = this.$util.handler;
    var ryInfo = this.utilPost.getLogin();
    if (ryInfo.isLogin) {
      this.search.telEn = ryInfo.TELEN;
    }
  },
  mounted() {
    const currentYear = new Date().getFullYear();
    const minYear = 2023;

    // 动态生成年度列表：从2023年到当前年
    const yearList = [];
    for (let year = minYear; year <= currentYear; year++) {
      yearList.push(year);
    }
    const reversed = [...yearList].reverse();
    this.yearList = reversed;
    // 加载赛事数据
    this.loadEventData();

    // 监听滚动事件
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    // 移除滚动监听
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    selectYear(year) {
      this.selectedYear = year;
      this.eventList = [];
      this.pageSet.currentPage = 1;
      this.loadEventData();
    },
    getItemData(SSID) {
      const busData = {
        SSID: SSID
      };

      const paramData = {
        headerData: {
          ssid: "",
          op: this.op,
          methodName: "getItemList"
        },
        busData: busData
      };

      this.utilPost.paramData = paramData;
      //alert(JSON.stringify(paramData));
      this.utilPost.sendPost().then(data => {
        if (!data.isSuccess) {
          alert(data.msg);
          return;
        }

        this.itemList = data.content;
      });
    },
    loadingGdHandler() {
      if (this.isEndPage) {
        return;
      }
      this.pageSet.currentPage = this.pageSet.currentPage + 1;
      this.loadEventData();
    },
    goBack() {
      this.$router.push("/meIndex");
    },
    getStatusClass(daysLeft) {
      if (daysLeft > 7) return "status-normal";
      if (daysLeft > 0) return "status-warning";
      return "status-expired";
    },
    getStatusText(daysLeft) {
      if (daysLeft > 7) return "报名中";
      if (daysLeft > 0) return "即将截止";
      return "已结束";
    },
    getPercentage(count, total) {
      if (total === 0) return 0;
      return Math.round((count / total) * 100);
    },
    getProgressPercentage(current, expected) {
      if (expected === 0) return 0;
      const percentage = Math.round((current / expected) * 100);
      return Math.min(percentage, 100); // 最多显示100%
    },
    getProgressClass(current, expected) {
      const percentage = (current / expected) * 100;
      if (percentage >= 90) return "progress-high";
      if (percentage >= 60) return "progress-medium";
      return "progress-low";
    },
    // Tab 切换
    onTabChange(name) {
      if (this.activeTab === "ongoing") {
        this.search.bszt = "1";
      } else {
        this.search.bszt = "7";
      }
      this.eventList = [];
      this.pageSet.currentPage = 1;
      this.loadEventData();
    },
    // 按报名时间排序
    sortByTime() {
      if (this.sortType === "time") {
        // 如果已经是按时间排序，则切换升序/降序
        this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
      } else {
        // 切换到按时间排序，默认降序
        this.sortType = "time";
        this.sortOrder = "desc";
      }
      this.eventList = [];
      this.pageSet.currentPage = 1;
      this.loadEventData();
    },
    // 按已报人数排序
    sortByPeople() {
      if (this.sortType === "people") {
        // 如果已经是按人数排序，则切换升序/降序
        this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
      } else {
        // 切换到按人数排序，默认降序
        this.sortType = "people";
        this.sortOrder = "desc";
      }
      this.eventList = [];
      this.pageSet.currentPage = 1;
      this.loadEventData();
    },
    // 按报名进度排序
    sortByProgress() {
      if (this.sortType === "progress") {
        // 如果已经是按进度排序，则切换升序/降序
        this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
      } else {
        // 切换到按进度排序，默认降序
        this.sortType = "progress";
        this.sortOrder = "desc";
      }
      this.eventList = [];
      this.pageSet.currentPage = 1;
      this.loadEventData();
    },
    loadEventData() {
      this.itemList = [];
      const busData = {
        pageSet: this.pageSet,
        search: this.search,
        sortType: this.sortType,
        sortOrder: this.sortOrder,
        year: this.selectedYear
      };

      const paramData = {
        headerData: {
          ssid: "",
          op: this.op,
          methodName: "getSsTj"
        },
        busData: busData
      };

      this.utilPost.paramData = paramData;
      //alert(JSON.stringify(paramData));
      this.utilPost.sendPost().then(data => {
        if (!data.isSuccess) {
          alert(data.msg);
          return;
        }

        for (var a = 0; a < data.content.length; a++) {
          this.eventList.push(data.content[a]);
        }

        if (
          data.content.length == 0 ||
          data.totalCount === this.pageSet.currentPage
        ) {
          this.isEndPage = true;
        } else {
          this.isEndPage = false;
        }
        this.isLoading = false;
      });
    },
    // 下拉刷新
    onRefresh() {
      this.eventList = [];
      this.pageSet.currentPage = 1;
      this.loadEventData();
    },
    // 监听滚动
    handleScroll() {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop;
      // 滚动超过300px时显示返回顶部按钮
      this.showBackTop = scrollTop > 300;
      // 判断是否在顶部（允许10px的误差）
      this.isAtTop = scrollTop <= 10;
    },
    // 返回顶部
    scrollToTop() {
      // 平滑滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    // 搜索功能
    onSearch() {
      this.eventList = [];
      this.pageSet.currentPage = 1;
      this.loadEventData();
    }
  }
};
</script>

<style scoped>
.event-statistics-page {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: 10px;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #2d8cf0;
  font-size: 15px;
  cursor: pointer;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.header-right {
  width: 60px;
}

/* 搜索框样式 */
.search-container {
  background: #fff;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.search-action {
  color: #2d8cf0;
  font-size: 14px;
  font-weight: 500;
  padding: 0 10px;
  cursor: pointer;
}

.search-action:active {
  opacity: 0.7;
}

/* Tab 样式 */
.tab-title {
  display: flex;
  align-items: center;
  gap: 4px;
}

.tab-title .van-icon {
  font-size: 16px;
}

.tab-title .van-badge {
  margin-left: 4px;
}

/* 排序按钮 */
.sort-buttons {
  display: flex;
  gap: 6px;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.sort-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 6px 8px;
  background: #f5f7fa;
  border: 1px solid #e8eaed;
  border-radius: 16px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
}

.sort-btn .van-icon {
  font-size: 13px;
  flex-shrink: 0;
}

.sort-btn span {
  flex-shrink: 0;
}

.sort-btn:active {
  transform: scale(0.98);
}

.sort-btn.active {
  background: linear-gradient(135deg, #2d8cf0 0%, #4facfe 100%);
  border-color: #2d8cf0;
  color: #fff;
  box-shadow: 0 2px 8px rgba(45, 140, 240, 0.3);
}

.sort-btn.active .van-icon {
  color: #fff;
}

.sort-icon {
  font-size: 14px;
  color: #fff;
  margin-left: 4px;
  animation: bounce 0.3s ease;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

/* 赛事列表 */
.event-list {
  margin-top: 10px;
  padding: 0 16px;
}

.event-card {
  width: 100%;
  padding: 10px 0;
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.event-name {
  font-size: 17px;
  font-weight: 600;
  color: #333;
  margin: 0;
  flex: 1;
}

.status-tag {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-normal {
  background: #e8f5e9;
  color: #19be6b;
}

.status-warning {
  background: #fff3e0;
  color: #ff9900;
}

.status-expired {
  background: #ffebee;
  color: #ed4014;
}

/* 统计项 */
.event-stats {
  margin-bottom: 10px;
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 5px 0;
  font-size: 14px;
}

.stat-item .van-icon {
  font-size: 16px;
  margin-right: 8px;
}

.stat-label {
  color: #666;
  margin-right: 4px;
}

.stat-value {
  color: #333;
  font-weight: 500;
}

.stat-value.primary {
  color: #ff9900;
  font-weight: 600;
  font-size: 15px;
}

.stat-value.total {
  color: #e53935;
  font-weight: 700;
  font-size: 16px;
}

.stat-item.total-amount {
  background: linear-gradient(90deg, #ffebee 0%, #fce4ec 100%);
  margin: 0 -5px;
  padding: 5px 10px;
  border-radius: 6px;
  border-left: 3px solid #e53935;
}

/* 场馆和费用信息 */
.venue-info {
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
  border-left: 3px solid #19be6b;
}

.info-row {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #333;
  margin-bottom: 6px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .van-icon {
  font-size: 15px;
  margin-right: 6px;
  color: #19be6b;
}

.info-label {
  color: #666;
  margin-right: 4px;
  min-width: 70px;
}

.info-value {
  color: #333;
  font-weight: 500;
}

.info-value.highlight {
  color: #ff6b00;
  font-weight: 600;
  font-size: 14px;
}

/* 时间信息 */
.event-time {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
}

.time-row {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;
  margin-bottom: 5px;
}

.time-row:last-child {
  margin-bottom: 0;
}

.time-row .van-icon {
  font-size: 14px;
  margin-right: 6px;
  color: #999;
}

/* 倒计时 */
.days-left {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: #fff9e6;
  border-radius: 8px;
  font-size: 14px;
  color: #ff9900;
}

.days-left .van-icon {
  font-size: 16px;
  margin-right: 6px;
}

.days-left strong {
  color: #ff6b00;
  font-size: 16px;
  margin: 0 4px;
}

.days-left .expired {
  color: #999;
}

/* 折叠详情 */
.event-detail {
  padding: 10px;
  background: #fafbfc;
}

.detail-header {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8eaed;
}

.detail-header .van-icon {
  font-size: 18px;
  margin-right: 8px;
  color: #2d8cf0;
}

/* 项目列表 */
.category-list {
  margin-bottom: 22px;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.category-name {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.category-name .van-icon {
  font-size: 16px;
  margin-right: 8px;
}

.category-count {
  display: flex;
  align-items: baseline;
}

.count-number {
  font-size: 20px;
  font-weight: 600;
  color: #2d8cf0;
  margin-right: 4px;
}

.count-unit {
  font-size: 12px;
  color: #999;
}

/* 图表区域 */
.chart-section {
  background: #fff;
  border-radius: 12px;
  padding: 10px;
}

.chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.chart-bars {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chart-bar-item {
  display: grid;
  grid-template-columns: 70px 1fr 50px;
  align-items: center;
  gap: 12px;
}

.bar-label {
  font-size: 13px;
  color: #666;
  text-align: left;
}

.bar-container {
  height: 20px;
  background: #f0f2f5;
  border-radius: 10px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #2d8cf0 0%, #4facfe 100%);
  border-radius: 10px;
  transition: width 0.5s ease;
}

.bar-percentage {
  font-size: 13px;
  color: #2d8cf0;
  font-weight: 600;
  text-align: right;
}

/* 报名进度条 */
.registration-progress {
  margin-top: 8px;
  padding: 8px 10px;
  background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
  border-radius: 8px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.progress-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.progress-text {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.progress-bar {
  height: 24px;
  background: #e8eaed;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
}

.progress-fill {
  height: 100%;
  border-radius: 12px;
  transition: width 0.6s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
}

/* 进度条颜色 - 低进度（<60%） */
.progress-low {
  background: linear-gradient(90deg, #ff6b6b 0%, #ff8e8e 100%);
}

/* 进度条颜色 - 中等进度（60%-90%） */
.progress-medium {
  background: linear-gradient(90deg, #ffa726 0%, #ffb74d 100%);
}

/* 进度条颜色 - 高进度（>=90%） */
.progress-high {
  background: linear-gradient(90deg, #19be6b 0%, #4cd964 100%);
}

/* 进度条动画效果 */
.progress-fill::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 折叠面板样式 */
.event-list >>> .van-collapse-item {
  margin-bottom: 8px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 22px;
  border-bottom: 8px solid #000000;
  border-top: 8px solid green;
}

.event-list >>> .van-collapse-item__title {
  padding: 0 16px;
}

.event-list >>> .van-collapse-item__content {
  padding: 0;
  background: #fafbfc;
}

.event-list >>> .van-cell::after {
  display: none;
}

/* 返回顶部按钮 */
.back-top-btn {
  position: fixed;
  right: 20px;
  bottom: 80px;
  width: 50px;
  height: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2d8cf0 0%, #4facfe 100%);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.4);
  cursor: pointer;
  z-index: 999;
  transition: all 0.3s ease;
}

.back-top-btn:active {
  transform: scale(0.95);
}

.back-top-btn .van-icon {
  font-size: 20px;
  color: #fff;
  margin-bottom: 2px;
}

.back-top-btn span {
  font-size: 10px;
  color: #fff;
  font-weight: 500;
}

/* 淡入淡出动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 响应式 */
@media (max-width: 375px) {
  .event-name {
    font-size: 16px;
  }

  .stat-item {
    font-size: 13px;
  }

  .back-top-btn {
    width: 45px;
    height: 45px;
    right: 15px;
    bottom: 70px;
  }

  .back-top-btn .van-icon {
    font-size: 18px;
  }

  .back-top-btn span {
    font-size: 9px;
  }
}
/* 年度筛选样式 */
.year-filter {
  background: #fff;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  overflow: hidden;
}

.year-list {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 0 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.year-list::-webkit-scrollbar {
  display: none;
}

.year-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 20px;
  transition: all 0.3s ease;
  background: #f5f7fa;
  flex-shrink: 0;
}

.year-item span {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  white-space: nowrap;
  transition: all 0.3s ease;
}

.year-item.active {
  background: linear-gradient(135deg, #2d8cf0 0%, #4facfe 100%);
  box-shadow: 0 3px 8px rgba(45, 140, 240, 0.3);
}

.year-item.active span {
  color: #fff;
  font-weight: 600;
}

.year-indicator {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  background: #2d8cf0;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}
</style>



// WEBPACK FOOTER //
// src/components/MeIndex/EventStatistics.vue